
import React, { useEffect } from 'react';
import { GenericScheduleProps, Schedule16ConnectedPersonsData, ConnectedPersonPaymentItem, FormErrorsStep } from '../../types';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';
import { safeParseFloat } from '../../utils/calculations';
import { translations } from '../../i18n/translations';

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h4 className="text-md font-semibold text-sky-700 mt-4 mb-2 border-b pb-1">{title}</h4>
);

export const Schedule16_2_ConnectedPersons: React.FC<GenericScheduleProps<Schedule16ConnectedPersonsData>> = ({ scheduleData, onChange, errors, t, fullFormData }) => {
  
  const handleItemChange = (index: number, field: keyof ConnectedPersonPaymentItem, value: string) => {
    const updatedItems = scheduleData.payments.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    onChange('payments', updatedItems);
  };

  const addItem = () => {
    onChange('payments', [
      ...scheduleData.payments,
      { 
        id: Date.now().toString(), 
        name: '', 
        trnOrTin: '', 
        paymentOrBenefitType: '', 
        description: '', 
        valueProvidedByTP: '', 
        marketValue: ''
      }
    ]);
  };

  const removeItem = (id: string) => {
    onChange('payments', scheduleData.payments.filter(item => item.id !== id));
  };

  const paymentTypeOptions: { value: ConnectedPersonPaymentItem['paymentOrBenefitType'], label: string }[] = [
    { value: '', label: t('select_option') },
    { value: 'remuneration', label: t('sch16_2_pt_remuneration') },
    { value: 'interest', label: t('sch16_2_pt_interest') },
    { value: 'royalties', label: t('sch16_2_pt_royalties') },
    { value: 'other_services', label: t('sch16_2_pt_other_services') },
    { value: 'other_in_kind', label: t('sch16_2_pt_other_in_kind') },
    { value: 'other', label: t('sch16_2_pt_other') },
  ];

  useEffect(() => {
    let totalAdjustmentsCalc = 0;

    const updatedPayments = scheduleData.payments.map(item => {
      const valueProvided = safeParseFloat(item.valueProvidedByTP);
      const marketVal = safeParseFloat(item.marketValue);
      const adjustment = valueProvided - marketVal; 
      
      if (adjustment > 0) { 
        totalAdjustmentsCalc += adjustment;
      }
      return { ...item, adjustment: adjustment.toFixed(2) };
    });

    if (JSON.stringify(updatedPayments) !== JSON.stringify(scheduleData.payments)) {
      onChange('payments', updatedPayments);
    }
    if (scheduleData.totalAdjustments !== totalAdjustmentsCalc.toFixed(2)) {
      onChange('totalAdjustments', totalAdjustmentsCalc.toFixed(2));
    }

  }, [scheduleData.payments, onChange, scheduleData.totalAdjustments]);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-sky-600">{t('sch16_2_title')}</h3>
      <p className="text-sm text-gray-600">{t('sch16_2_note_thresholds')}</p>
      
      {scheduleData.payments.map((item, index) => (
        <div key={item.id} className="p-3 border rounded-md space-y-3 bg-slate-50 relative">
            <p className="text-sm font-semibold text-gray-600">{t('sch16_2_payment_item_heading', {index: index + 1})}</p>
            
            <Input label={t('sch16_2_cp_name_label')} type="text" id={`s16cpName-${item.id}`} value={item.name} onChange={(e) => handleItemChange(index, 'name', e.target.value)} error={(errors?.payments as FormErrorsStep[])?.[index]?.name as string}/>
            <Input label={t('sch16_2_cp_trn_tin_label')} type="text" id={`s16cpTrnTin-${item.id}`} value={item.trnOrTin} onChange={(e) => handleItemChange(index, 'trnOrTin', e.target.value)} error={(errors?.payments as FormErrorsStep[])?.[index]?.trnOrTin as string}/>
            <Select label={t('sch16_2_payment_type_label')} id={`s16cpPaymentType-${item.id}`} value={item.paymentOrBenefitType} options={paymentTypeOptions} onChange={(e) => handleItemChange(index, 'paymentOrBenefitType', e.target.value as ConnectedPersonPaymentItem['paymentOrBenefitType'])} error={(errors?.payments as FormErrorsStep[])?.[index]?.paymentOrBenefitType as string}/>
            <Input label={t('sch16_2_description_label')} type="text" id={`s16cpDesc-${item.id}`} value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} error={(errors?.payments as FormErrorsStep[])?.[index]?.description as string}/>
            <Input label={t('sch16_2_value_provided_by_tp_label')} type="number" id={`s16cpValueProvided-${item.id}`} value={item.valueProvidedByTP} onChange={(e) => handleItemChange(index, 'valueProvidedByTP', e.target.value)} error={(errors?.payments as FormErrorsStep[])?.[index]?.valueProvidedByTP as string}/>
            <Input label={t('sch16_2_market_value_label')} type="number" id={`s16cpMarketValue-${item.id}`} value={item.marketValue} onChange={(e) => handleItemChange(index, 'marketValue', e.target.value)} error={(errors?.payments as FormErrorsStep[])?.[index]?.marketValue as string}/>
            <Input label={t('sch16_2_adjustment_label')} type="text" id={`s16cpAdjustment-${item.id}`} value={item.adjustment || '0.00'} readOnly className="bg-gray-100" helpText={t('sch16_2_adjustment_help')}/>

            <Button onClick={() => removeItem(item.id)} variant="danger_outline" size="sm" className="absolute top-2 right-2">{t('remove_item_button')}</Button>
        </div>
      ))}
      <Button onClick={addItem} variant="secondary" size="sm">{t('sch16_2_add_payment_button')}</Button>

      <Input label={t('sch16_2_total_adjustments_label')} type="text" id="s16TotalCpAdj" value={scheduleData.totalAdjustments || '0.00'} readOnly className="bg-gray-100 font-semibold" />
    </div>
  );
};
